import React from 'react';
import { Crown, Shield, Star, Coffee, Zap, Circle } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const ROLE_BADGES = {
  superadmin: { label: 'ADMIN', cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
  executive: { label: 'EXEC', cls: 'bg-purple-500/20 text-purple-400 border border-purple-500/30' },
  admin: { label: 'ADMIN', cls: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' },
  manager: { label: 'MGR', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
  staff: { label: 'STAFF', cls: 'bg-slate-600/30 text-slate-400 border border-slate-600/30' },
};

const ChatMembersPanel = ({ staffList, onlineStatus, currentUser, onUserClick, groupMembers }) => {
  const isOnline = (username) => {
    const s = onlineStatus[username];
    if (!s) return false;
    const lastActive = new Date(s.last_active);
    return (Date.now() - lastActive.getTime()) < 300000;
  };

  const members = groupMembers
    ? staffList.filter(s => groupMembers.includes(s.username))
    : staffList;

  const onlineMembers = members.filter(s => isOnline(s.username));
  const offlineMembers = members.filter(s => !isOnline(s.username));

  const renderMember = (staff) => {
    const online = isOnline(staff.username);
    const badge = ROLE_BADGES[staff.role] || ROLE_BADGES.staff;
    return (
      <button key={staff.username} onClick={() => onUserClick(staff.username)}
        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-800/60 transition-colors group"
        data-testid={`member-${staff.username}`}>
        <div className="relative flex-shrink-0">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center">
            {staff.avatar ? (
              <img src={getImageUrl(staff.avatar)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className={`text-xs font-semibold ${online ? 'text-white' : 'text-slate-500'}`}>
                {(staff.display_name || staff.username).charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${
            online ? 'bg-emerald-500' : 'bg-slate-600'
          }`} />
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className={`text-sm font-medium truncate ${online ? 'text-slate-200' : 'text-slate-500'}`}>
            {staff.display_name || staff.username}
          </span>
          <span className={`text-[9px] px-1 py-px rounded font-bold uppercase tracking-wider ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className="w-60 flex-shrink-0 bg-slate-900/40 border-l border-slate-800/50 overflow-y-auto" data-testid="members-panel">
      <div className="p-3">
        {onlineMembers.length > 0 && (
          <>
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-1 px-2">
              Online — {onlineMembers.length}
            </p>
            <div className="space-y-0.5 mb-4">
              {onlineMembers.map(renderMember)}
            </div>
          </>
        )}
        {offlineMembers.length > 0 && (
          <>
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-1 px-2">
              Offline — {offlineMembers.length}
            </p>
            <div className="space-y-0.5">
              {offlineMembers.map(renderMember)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { ROLE_BADGES };
export default ChatMembersPanel;
